/**
 * Boot hand-off between the splash overlay and the first screen.
 *
 * The splash is ONE persistent overlay (BootOverlay) above the router. The first screen mounts and lays out
 * underneath it while it plays, so the reveal never shows a half-loaded page. The overlay leaves once:
 *  - the session is ready (or failed), and
 *  - the first screen has rendered (`markScreenMounted`), its fonts loaded and its images decoded, and
 *  - the iOS minimum splash duration (2.4 s since first paint, `window.__dtBootT0`) has elapsed.
 */
import { useSyncExternalStore } from 'react';

export type BootPhase = 'booting' | 'revealing' | 'done';

interface BootState { sessionReady: boolean; screenMounted: boolean; phase: BootPhase }

let state: BootState = { sessionReady: false, screenMounted: false, phase: 'booting' };
const listeners = new Set<() => void>();

function set(patch: Partial<BootState>) {
  const next = { ...state, ...patch };
  if (next.sessionReady === state.sessionReady && next.screenMounted === state.screenMounted && next.phase === state.phase) return;
  state = next;
  for (const l of listeners) l();
}

export const markSessionReady = () => set({ sessionReady: true });
export const markScreenMounted = () => set({ screenMounted: true });
export const markRevealing = () => set({ phase: 'revealing' });
export const markBootDone = () => set({ phase: 'done' });

export function useBootState(): BootState {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => state,
    () => state,
  );
}

/** True while the splash still covers the screen (first-screen entrance animations wait for this). */
export function useBootCovering(): boolean {
  return useBootState().phase === 'booting';
}

declare global { interface Window { __dtBootT0?: number } }

/** Milliseconds since the first paint of the inline splash (index.html), else since navigation start. */
export function sinceBoot(): number {
  const t0 = typeof window !== 'undefined' && typeof window.__dtBootT0 === 'number' ? window.__dtBootT0 : 0;
  return performance.now() - t0;
}

/** Test helper. */
export function resetBootStore() { state = { sessionReady: false, screenMounted: false, phase: 'booting' }; }
